import { useDispatch, useSelector } from "react-redux";
import { Styles } from "../styles/retailersList";
import { TextBoxWithIcon } from "./textBoxWithIcon";
import { useEffect, useState } from "react";
// No longer importing Constants for translatable strings
import { useNavigate, useSearchParams } from "react-router-dom";
import { colors } from "../styles/standards";
// Ensure these components use t() internally
import { NotAuthorized } from "./notAuthorized";
import { NoStores } from "./noSTorsfound";
import { fetchAllRetailers } from "../store/slices/retailersList";
import { resetFOTandSubDistrict } from "../store/slices/subDistricts";
import { Arrow, Arrow_Disabled } from "../constants"; // Assuming these are image paths or similar
import { useTranslation } from '../i18n/useTranslation'; // Import the useTranslation hook

// Moved filterconfig labels to use translation keys
const filterconfig = [
  {
    label: "all", // This will be a translation key
    isactive: true,
    count: 0
  },
  {
    label: "open", // This will be a translation key
    isactive: false,
    count: 0
  },
  {
    label: "in_progress", // This will be a translation key
    isactive: false,
    count: 0
  },
  {
    label: "closed", // This will be a translation key
    isactive: false,
    count: 0
  },
];

const surveyStatuses = {
  inprogress: "in_progress",
  completed: "closed",
  open: "open" // Added 'open' status for completeness
};

const RetailerRow = ({ retailer, setPredefinedFilter }) => {
  const [searchParams] = useSearchParams();
  const queryParams = Object.fromEntries(searchParams.entries());
  const { t } = useTranslation(); // Initialize useTranslation

  let parsedParameters = {
    territory_id: queryParams.territory_id,
    user_id: queryParams.fo_id,
    fo_phone_number: queryParams.fo_phone_number,
  };

  const navigate = useNavigate();

  const reDirectToOtp = (data) => {
    setPredefinedFilter('all');
    localStorage.setItem('mobile_number', retailer.mobile_number);
    localStorage.setItem('customer_id', retailer.customer_id);
    navigate("/otp", { state: { retailer: data, parsedParameters } });
  };

  // It seems pointerEvents logic is based on fo_id. No translation needed here.
  const rowStyles = {
    ...Styles.retailerRow,
    pointerEvents: retailer.fo_id === parsedParameters.user_id ? "all" : "none",
  };

  return (
    <div
      style={retailer.fo_id ? rowStyles : Styles.retailerRow}
      onClick={async () => {
        // Only allow click if the survey is not completed or if it's the current user's survey in progress
        if (retailer.survey_status && retailer.survey_status !== surveyStatuses.completed) {
          if (retailer.survey_status === surveyStatuses.inprogress) {
            await localStorage.setItem('mobile_number', retailer.mobile_number);
            await localStorage.setItem('brp_mobile_number', retailer.mobile_number);
            await localStorage.setItem('customer_id', retailer.customer_id);
            await localStorage.setItem('createnewuser', "false");
            navigate("/step1", {
              state: {
                isNewUser: true,
                retailer: retailer,
                parsedParameters: parsedParameters,
                territoryID: parsedParameters.territory_id,
                userID: parsedParameters.user_id ? parsedParameters.user_id : queryParams.fo_id,
              },
            });
          } else {
            reDirectToOtp(retailer);
          }
        }
      }}
    >
      <div style={Styles.arrowContainer}>
        <span style={Styles.retailerName}>{retailer.customer_name}</span>
        {retailer.survey_status && retailer.survey_status === surveyStatuses.completed ? (
          <img
            src={Arrow_Disabled}
            style={{ width: 30, height: 30 }}
            alt="loader"
          />
        ) : retailer.fo_id !== parsedParameters.user_id &&
          retailer.survey_status === surveyStatuses.inprogress ? (
          <img
            src={Arrow_Disabled}
            style={{ width: 30, height: 30 }}
            alt="loader"
          />
        ) : (
          <img src={Arrow} style={{ width: 30, height: 30 }} alt="loader" />
        )}
      </div>

      <span style={Styles.area}>
        {t('survey_status_ter')}: {retailer.territory}, {t('survey_status_fot')}: {retailer.fot}
      </span>
      <span style={Styles.phoneNumber}>{retailer.mobile_number}</span>
      {(retailer.survey_status === surveyStatuses.inprogress ||
        retailer.survey_status === surveyStatuses.completed) && (
        <span
          style={{
            ...Styles.statusCapsule,
            background:
              retailer.survey_status === surveyStatuses.inprogress
                ? "#49c3f6"
                : retailer.survey_status === surveyStatuses.completed
                ? "#efefef"
                : "",
            color:
              retailer.survey_status === surveyStatuses.inprogress
                ? "#2585c3"
                : retailer.survey_status === surveyStatuses.completed
                ? "#767676"
                : "",
          }}
        >
          {t('survey')} {t(retailer.survey_status)} {/* Translate 'Survey' and the status */}
        </span>
      )}
    </div>
  );
};

export const RetailersList = ({ parsedParameters }) => {
  const retailers = useSelector((state) => state.retailers);
  const fot = useSelector((state) => state.subDist);
  const [filtered, setFiltered] = useState([]);
  const [surveyFilter, setSurveyFilter] = useState(filterconfig); // Uses the updated filterconfig
  const dispatch = useDispatch();
  const { t } = useTranslation(); // Initialize useTranslation

  useEffect(() => {
    if (fot.selectedFOTvalue) {
      setFiltered(
        retailers.retailers.filter((item) => item.fot_id == fot.selectedFOTvalue)
      );
    } else {
      // If FOT is deselected, reset filter to all
      setFiltered(retailers.retailers);
    }
    getStatusCount(); // Recalculate counts when filter changes
  }, [fot.selectedFOTvalue, retailers.retailers]); // Added retailers.retailers to dependencies

  useEffect(() => {
    if (fot.selectedSubDist) {
      setFiltered(
        retailers.retailers.filter(
          (item) =>
            item.sub_district == fot.selectedSubDist &&
            item.fot_id == fot.selectedFOTvalue
        )
      );
    } else if (!fot.selectedFOTvalue) {
      // If no sub-district or FOT is selected, show all
      setFiltered(retailers.retailers);
    }
    getStatusCount(); // Recalculate counts when filter changes
  }, [fot.selectedSubDist, fot.selectedFOTvalue, retailers.retailers]); // Added retailers.retailers to dependencies

  const getStatusCount = () => {
    let surveyFiltercopy = surveyFilter.map(filter => ({ ...filter, count: 0 })); // Reset counts safely
    retailers.retailers.forEach(element => {
      // Use surveyStatuses object for robust comparison
      if(element.survey_status === surveyStatuses.open){
        surveyFiltercopy[1].count += 1;
      } else if(element.survey_status === surveyStatuses.inprogress){
        surveyFiltercopy[2].count += 1;
      } else if(element.survey_status === surveyStatuses.completed){
        surveyFiltercopy[3].count += 1;
      }
    });
    setSurveyFilter(surveyFiltercopy);
  }

  useEffect(() => {
    let copiedOBJ = [...retailers.retailers];
    let data = copiedOBJ.sort((a, b) => {
      if(a && a.shop_name && b && b.shop_name){
        return a.shop_name.toLowerCase().localeCompare(b.shop_name.toLowerCase());
      }
      return 0; // Handle cases where shop_name might be missing
    });
    setFiltered(data);
    getStatusCount();
  }, [retailers.retailers]); // Dependency changed to retailers.retailers

  const onChange = (data) => {
    dispatch(resetFOTandSubDistrict()); // Keep this as it resets filters
    if (data === "") {
      setFiltered(retailers.retailers);
    } else {
      const lowerCaseData = data.toLowerCase();
      setFiltered(
        retailers.retailers.filter(
          (item) =>
            (item.shop_name && item.shop_name.toLowerCase().includes(lowerCaseData)) ||
            (item.mobile_number && item.mobile_number.toLowerCase().includes(lowerCaseData)) ||
            (item.territory && item.territory.toLowerCase().includes(lowerCaseData))
        )
      );
    }
  };

  const setPredefinedFilter = (status) => {
    // This is a direct modification of filterconfig array, which might not re-render if it's the same array reference.
    // It's better to create a new array or use map to update state immutably.
    const updatedFilterConfig = filterconfig.map(item => ({
      ...item,
      isactive: item.label === status // Set active based on the new status
    }));

    let currentFilteredData = [...retailers.retailers];
    currentFilteredData.sort((a, b) => {
      if(a && a.shop_name && b && b.shop_name){
        return a.shop_name.toLowerCase().localeCompare(b.shop_name.toLowerCase());
      }
      return 0;
    });

    if (status === "all") {
      setFiltered(currentFilteredData);
    } else {
      setFiltered(
        currentFilteredData.filter((item) => item.survey_status === status)
      );
    }
    setSurveyFilter(updatedFilterConfig); // Update state with the new array
  };

  return (
    <div style={Styles.container}>
      {/* Ensure NoStores component is updated to use t() internally */}
      {filtered.length === 0 && !retailers.loading && (fot.selectedFOTvalue || fot.selectedSubDist) ? <NoStores onBack={() => { dispatch(resetFOTandSubDistrict()); window.location.reload(); }} /> : null} {/* Only show NoStores if actually filtered and nothing found */}
      {/* If you want to show NoStores when there are no retailers at all initially */}
      {retailers.retailers.length === 0 && !retailers.loading && !fot.selectedFOTvalue && !fot.selectedSubDist ? <NoStores /> : null}

      <div style={Styles.searchContainer}>
        <TextBoxWithIcon
          iconname={"fa fa-search"}
          handler={onChange}
          placeHolder={t('search_by_name_number')} // Translate the placeholder
        />
        <div style={Styles.filterCont}>
          {surveyFilter.map((item, i) => {
            return (
              <div
                key={i}
                onClick={() => {
                  setPredefinedFilter(item.label);
                }}
                style={{
                  ...Styles.filterCapsule,
                  background: item.isactive ? colors.Blue_Dark : colors.White,
                  color: item.isactive ? colors.White : colors.Black,
                  width: "20%",
                  textTransform: "capitalize",
                }}
              >
                {t(item.label)} {/* Translate the filter labels */}
                {item.count !==0 && <span style={Styles.counts}>
                  {item.count}
                </span>}

              </div>
            );
          })}
        </div>
        <span style={Styles.totalCount}>
          {t('total_count')}: {retailers.retailers.length} {/* Translate 'Total count' */}
        </span>
        <span
          style={Styles.syncnow}
          onClick={() => {
            setPredefinedFilter('all') // Reset filter to 'all' before syncing
            dispatch(fetchAllRetailers(parsedParameters));
          }}
        >
          {t('sync_now')} {/* Translate 'Sync Now' */}
        </span>
      </div>
      {/* Only render retailers if there are any, and if not loading */}
      {!retailers.loading && filtered.length > 0 && (
        filtered.map((retailer, i) => {
          return <RetailerRow retailer={retailer} setPredefinedFilter={setPredefinedFilter} key={i} />;
        })
      )}
    </div>
  );
};